import { ReservationPolicyInformation } from '@/models';

const HTML_ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&nbsp;': ' ',
};

// Inline formatting tags (bold/italic/anchor/…) carry no word boundary, so we
// remove them WITHOUT inserting whitespace — otherwise "<b>fully refundable</b>."
// collapses to "fully refundable ." with a stray space before the period on the
// primary EN/FR smart-policy text. Block-level and <br> tags become a single
// space so separate lines/paragraphs don't run together.
const INLINE_TAG =
  /<\/?(?:b|i|u|s|em|strong|span|a|small|sub|sup|mark|del|ins|code|abbr|cite|q|font)\b[^>]*>/gi;
const BLOCK_OR_OTHER_TAG = /<\/?[a-zA-Z][^>]*>/g;

/**
 * Strip HTML tags from hotel free-text and return plain text.
 * XSS-safety comes from React text-node escaping at render time; this only
 * removes markup so empty markup (e.g. "<p></p>") collapses to "" and no
 * literal tags are shown. Both tag patterns require a letter after "<" (or
 * "</") so real tags (<p>, <br/>, <b>) are removed while a stray comparison
 * in prose (e.g. "cancel if 2 < 3 nights") is preserved.
 */
export const stripHtmlToText = (value: string | null | undefined): string => {
  if (!value) {
    return '';
  }
  // Decode entities first so entity-encoded markup ("&lt;b&gt;") is also caught
  // by the tag pass below.
  const decoded = value.replace(
    /&amp;|&lt;|&gt;|&quot;|&#39;|&nbsp;/g,
    (match) => HTML_ENTITIES[match] ?? match,
  );
  // Strip tags repeatedly until the string is stable: a single pass can let
  // adjacent characters re-form a tag (e.g. "<scr<script>ipt>"), so keep
  // stripping until nothing changes (complete multi-character sanitization).
  let stripped = decoded;
  let previous: string;
  do {
    previous = stripped;
    stripped = previous
      .replace(INLINE_TAG, '')
      .replace(BLOCK_OR_OTHER_TAG, ' ');
  } while (stripped !== previous);
  return stripped.replace(/\s+/g, ' ').trim();
};

export type CancellationPolicyView = {
  label: string;
  cancellationText: string;
  additionalPolicyText: string;
};

/**
 * Map the API response to the fields the card renders, or null when there is
 * nothing meaningful to show (covers the documented 200 {policyText:null}
 * "no policy" response and all-empty text).
 */
export const getCancellationPolicyView = (
  info: ReservationPolicyInformation | null | undefined,
): CancellationPolicyView | null => {
  const policyText = info?.policyText;
  if (!policyText) {
    return null;
  }
  const label = stripHtmlToText(policyText.cancellationType?.text);
  const cancellationText = stripHtmlToText(policyText.cancellationText);
  const additionalPolicyText = stripHtmlToText(policyText.additionalPolicyText);
  if (!label && !cancellationText && !additionalPolicyText) {
    return null;
  }
  return { label, cancellationText, additionalPolicyText };
};
